import { Check, X } from "lucide-react";
import { Reveal } from "./motion";
import { HW_COMPARE, HW_SPECS, HW_SIZING, HW_TANKS, HW_CERTS } from "./data";

export const HotWaterSpecs = () => {
  return (
    <section id="specs" data-testid="hw-specs" className="relative py-28 lg:py-36 bg-[#050505] border-y border-[#1c1c1c]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Evacuated Tube Technology</p>
          <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white mb-4" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
            Australia's highest performer.
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed">Cylindrical evacuated tubes track the sun all day and hold their heat like a thermos — dramatically outperforming flat-panel collectors.</p>
        </Reveal>

        {/* Comparison: tube vs flat panel */}
        <Reveal>
          <div className="mt-12 overflow-x-auto rounded-3xl border border-[#262626]">
            <table className="w-full text-left border-collapse min-w-[720px]" data-testid="hw-compare-table">
              <thead>
                <tr className="bg-[#0d0d0d]">
                  <th className="py-5 px-6 font-mono-eng text-[11px] uppercase tracking-wider text-zinc-500 font-medium"> </th>
                  <th className="py-5 px-6 font-display font-semibold text-lg text-[#fcc41b]">Evacuated Tube</th>
                  <th className="py-5 px-6 font-display font-semibold text-lg text-zinc-500">Flat Panel</th>
                </tr>
              </thead>
              <tbody>
                {HW_COMPARE.map((r) => (
                  <tr key={r.label} className="border-t border-[#1c1c1c]">
                    <td className="py-5 px-6 font-display font-semibold text-white whitespace-nowrap align-top">{r.label}</td>
                    <td className="py-5 px-6 text-zinc-300 leading-relaxed align-top">
                      <span className="inline-flex items-start gap-2"><Check size={17} className="text-[#fcc41b] mt-1 shrink-0" />{r.tube}</span>
                    </td>
                    <td className="py-5 px-6 text-zinc-500 leading-relaxed align-top">
                      <span className="inline-flex items-start gap-2"><X size={16} className="text-zinc-600 mt-1 shrink-0" />{r.flat}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* Collector specs */}
        <Reveal>
          <div className="mt-20">
            <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight mb-8">Collector specifications</h3>
            <div className="overflow-x-auto rounded-3xl border border-[#262626]">
              <table className="w-full text-left border-collapse min-w-[720px]" data-testid="hw-spec-table">
                <thead>
                  <tr className="bg-[#0d0d0d] text-zinc-500 font-mono-eng text-[11px] uppercase tracking-wider">
                    <th className="py-4 px-5 font-medium">Collector</th>
                    <th className="py-4 px-5 font-medium">Absorber</th>
                    <th className="py-4 px-5 font-medium">Aperture</th>
                    <th className="py-4 px-5 font-medium">Gross Area</th>
                    <th className="py-4 px-5 font-medium">Fluid</th>
                    <th className="py-4 px-5 font-medium">Weight</th>
                    <th className="py-4 px-5 font-medium">W × L</th>
                  </tr>
                </thead>
                <tbody>
                  {HW_SPECS.map((s) => (
                    <tr key={s.tubes} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d] transition-colors duration-200">
                      <td className="py-4 px-5 font-display font-semibold text-[#fcc41b] whitespace-nowrap">{s.tubes}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.absorber}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.aperture}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.gross}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.fluid}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.weight}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.dims}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        {/* Sizing + tanks */}
        <div className="mt-20 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div>
              <h3 className="font-display font-semibold text-2xl text-white tracking-tight mb-6">System sizing guide</h3>
              <div className="rounded-3xl border border-[#262626] overflow-hidden" data-testid="hw-sizing-table">
                {HW_SIZING.map((r, i) => (
                  <div key={i} className={`flex items-center gap-4 p-5 ${i !== 0 ? "border-t border-[#1c1c1c]" : ""}`}>
                    <span className="font-display font-bold text-2xl text-[#fcc41b] w-24 shrink-0">{r.tank}</span>
                    <div>
                      <div className="text-white font-display font-medium">{r.collector}</div>
                      <div className="text-zinc-500 text-sm">{r.suits}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div>
              <h3 className="font-display font-semibold text-2xl text-white tracking-tight mb-6">Tank options</h3>
              <div className="space-y-4">
                {HW_TANKS.map((t) => (
                  <div key={t.type} className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-6">
                    <div className="font-display font-semibold text-xl text-white mb-1">{t.type}</div>
                    <div className="font-mono-eng text-[#fcc41b]">{t.sizes}</div>
                  </div>
                ))}
                <p className="text-zinc-500 text-sm leading-relaxed">Optional gas boosting and tilt frames available. Electric boosting supported. Suited to frost-prone and alpine areas.</p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Certifications */}
        <Reveal>
          <div className="mt-16 flex flex-wrap gap-3">
            {HW_CERTS.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 rounded-full border border-[#262626] bg-[#0d0d0d] px-5 py-2.5 text-sm text-zinc-300">
                <span className="h-1.5 w-1.5 rounded-full bg-[#fcc41b]" />{c}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
